import * as React from 'react'
import {
    Box,
    Typography,
    Checkbox,
    IconButton,
    Paper,
    Chip,
    FormControlLabel,
} from '@mui/material'
import {
    ExpandMore,
    ExpandLess,
    ChevronRight,
    DataObject,
    DataArray,
    TextFields,
    Numbers,
    ToggleOn,
    CalendarToday,
} from '@mui/icons-material'

const isPlainObject = value =>
    value !== null && typeof value === 'object' && !Array.isArray(value)

// Merge several JSON records into one "schema sample" for the field tree.
// NDJSON logs often start with small control objects; later lines add `message`,
// `attachment`, etc. Using only the first row hides those paths in the UI.
function mergeJsonRecordsForFieldTree(records, maxRecords = 200) {
    if (!Array.isArray(records) || records.length === 0) return null

    const slice = records.slice(0, maxRecords).filter(isPlainObject)
    if (slice.length === 0) return null

    const mergeValues = (a, b) => {
        if (a === undefined || a === null) return b
        if (b === undefined || b === null) return a

        if (isPlainObject(a) && isPlainObject(b)) {
            const keys = new Set([...Object.keys(a), ...Object.keys(b)])
            const out = {}
            keys.forEach(key => {
                if (key in a && key in b) {
                    out[key] = mergeValues(a[key], b[key])
                } else if (key in a) {
                    out[key] = a[key]
                } else {
                    out[key] = b[key]
                }
            })
            return out
        }

        if (Array.isArray(a) && Array.isArray(b)) {
            const a0 = a[0]
            const b0 = b[0]
            if (
                isPlainObject(a0) &&
                isPlainObject(b0)
            ) {
                return [mergeValues(a0, b0)]
            }
            return a.length ? a : b
        }

        // Prefer richer shapes so nested paths (e.g. message.content[*].text) appear.
        if (
            typeof a === 'string' &&
            Array.isArray(b) &&
            b.some(item => isPlainObject(item))
        ) {
            return b
        }
        if (
            typeof b === 'string' &&
            Array.isArray(a) &&
            a.some(item => isPlainObject(item))
        ) {
            return a
        }

        return a
    }

    return slice.reduce((acc, row) => mergeValues(acc, row), {})
}

// Helper to determine field type and icon
const getFieldTypeInfo = value => {
    if (value === null || value === undefined) {
        return {
            type: 'null',
            icon: <TextFields fontSize="small" />,
            color: 'text.disabled',
        }
    }
    if (Array.isArray(value)) {
        return {
            type: 'array',
            icon: <DataArray fontSize="small" />,
            color: 'warning.main',
        }
    }
    if (typeof value === 'object') {
        return {
            type: 'object',
            icon: <DataObject fontSize="small" />,
            color: 'info.main',
        }
    }
    if (typeof value === 'boolean') {
        return {
            type: 'boolean',
            icon: <ToggleOn fontSize="small" />,
            color: 'success.main',
        }
    }
    if (typeof value === 'number') {
        return {
            type: 'number',
            icon: <Numbers fontSize="small" />,
            color: 'primary.main',
        }
    }
    if (typeof value === 'string') {
        // Check if it looks like a date
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
            return {
                type: 'date',
                icon: <CalendarToday fontSize="small" />,
                color: 'secondary.main',
            }
        }
        return {
            type: 'string',
            icon: <TextFields fontSize="small" />,
            color: 'text.primary',
        }
    }
    return {
        type: 'unknown',
        icon: <TextFields fontSize="small" />,
        color: 'text.secondary',
    }
}

// Component for rendering a single field/node in the tree
const JSONFieldNode = ({
    fieldPath,
    fieldName,
    value,
    level = 0,
    onToggle,
    isSelected,
    selectedPaths,
    onPreviewClick,
}) => {
    const [expanded, setExpanded] = React.useState(level < 2) // Auto-expand first 2 levels
    const fieldInfo = getFieldTypeInfo(value)
    const hasChildren =
        fieldInfo.type === 'object' || fieldInfo.type === 'array'
    const isSelectable = !hasChildren || fieldInfo.type === 'array'

    const handleToggle = () => {
        if (isSelectable) {
            onToggle(fieldPath)
        }
    }

    const renderValue = () => {
        if (fieldInfo.type === 'array') {
            const itemType =
                value.length > 0
                    ? typeof value[0] === 'object'
                        ? `${Object.keys(value[0]).length} fields each`
                        : typeof value[0]
                    : 'empty'

            return (
                <Box sx={{ ml: 1 }}>
                    <Chip
                        label={`Array[${value.length}] - ${itemType}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        onClick={() =>
                            onPreviewClick && onPreviewClick(fieldPath, value)
                        }
                        sx={{ cursor: 'pointer' }}
                    />
                </Box>
            )
        }
        if (fieldInfo.type === 'object') {
            const keys = Object.keys(value)
            return (
                <Chip
                    label={`Object{${keys.length} fields}`}
                    size="small"
                    color="info"
                    variant="outlined"
                />
            )
        }
        if (fieldInfo.type === 'boolean') {
            return (
                <Chip
                    label={value ? 'true' : 'false'}
                    size="small"
                    color={value ? 'success' : 'default'}
                    variant="outlined"
                />
            )
        }

        // For primitive values, show a preview
        const displayValue = String(value).substring(0, 50)
        return (
            <Typography
                variant="caption"
                sx={{
                    ml: 1,
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    wordBreak: 'break-word',
                }}
            >
                "{displayValue}
                {String(value).length > 50 ? '...' : ''}"
            </Typography>
        )
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 0.5,
                    pl: level * 3,
                    pr: 1,
                    '&:hover': {
                        backgroundColor: 'action.hover',
                    },
                    borderRadius: 1,
                    cursor: isSelectable ? 'pointer' : 'default',
                }}
                onClick={handleToggle}
            >
                {hasChildren && (
                    <IconButton
                        size="small"
                        onClick={e => {
                            e.stopPropagation()
                            setExpanded(!expanded)
                        }}
                        sx={{ p: 0, mr: 0.5 }}
                    >
                        {expanded ? (
                            <ExpandMore fontSize="small" />
                        ) : (
                            <ChevronRight fontSize="small" />
                        )}
                    </IconButton>
                )}
                {!hasChildren && <Box sx={{ width: 20 }} />}

                {isSelectable && (
                    <Checkbox
                        checked={isSelected}
                        size="small"
                        onClick={e => e.stopPropagation()}
                        onChange={() => onToggle(fieldPath)}
                        sx={{ p: 0.5 }}
                    />
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Box sx={{ color: fieldInfo.color, mr: 0.5 }}>
                        {fieldInfo.icon}
                    </Box>
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: 'monospace',
                            fontWeight: isSelected ? 'bold' : 'normal',
                        }}
                    >
                        {fieldName}
                    </Typography>
                    {renderValue()}
                </Box>
            </Box>

            {hasChildren && expanded && (
                <Box>
                    {fieldInfo.type === 'object' &&
                        Object.entries(value).map(([key, val]) => (
                            <JSONFieldNode
                                key={`${fieldPath}.${key}`}
                                fieldPath={`${fieldPath}.${key}`}
                                fieldName={key}
                                value={val}
                                level={level + 1}
                                onToggle={onToggle}
                                isSelected={selectedPaths.includes(
                                    `${fieldPath}.${key}`
                                )}
                                selectedPaths={selectedPaths}
                                onPreviewClick={onPreviewClick}
                            />
                        ))}
                    {fieldInfo.type === 'array' &&
                        value.length > 0 &&
                        typeof value[0] === 'object' && (
                            <Box sx={{ pl: (level + 1) * 3, py: 1 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 1 }}
                                >
                                    Showing first item of {value.length} total
                                    items. Selected fields will be applied to
                                    all items.
                                </Typography>
                                <Box
                                    sx={{
                                        pl: 1,
                                        borderLeft: '2px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    {typeof value[0] === 'object' &&
                                        Object.entries(
                                            value[0]
                                        ).map(([key, val]) => (
                                            <JSONFieldNode
                                                key={`${fieldPath}[*].${key}`}
                                                fieldPath={`${fieldPath}[*].${key}`}
                                                fieldName={key}
                                                value={val}
                                                level={level + 1}
                                                onToggle={onToggle}
                                                isSelected={selectedPaths.includes(
                                                    `${fieldPath}[*].${key}`
                                                )}
                                                selectedPaths={selectedPaths}
                                                onPreviewClick={onPreviewClick}
                                            />
                                        ))}
                                </Box>
                            </Box>
                        )}
                </Box>
            )}
        </Box>
    )
}

// Main component for JSON field selection with tree view
export const JSONFieldSelector = ({
    jsonData,
    selectedFields,
    onFieldToggle,
    onPreviewClick,
    title = 'Select Fields',
    helperText,
}) => {
    const [searchTerm, setSearchTerm] = React.useState('')

    const sampleData = React.useMemo(() => {
        if (jsonData === null || jsonData === undefined) return null
        if (Array.isArray(jsonData)) {
            return mergeJsonRecordsForFieldTree(jsonData)
        }
        return jsonData
    }, [jsonData])

    if (!sampleData) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography color="text.secondary">
                    No data available
                </Typography>
            </Box>
        )
    }

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {title}
                </Typography>
                {helperText && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        {helperText}
                    </Typography>
                )}
            </Box>

            <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                <Box>
                    {Object.entries(sampleData)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([key, value]) => (
                        <JSONFieldNode
                            key={key}
                            fieldPath={key}
                            fieldName={key}
                            value={value}
                            level={0}
                            onToggle={onFieldToggle}
                            isSelected={selectedFields.includes(key)}
                            selectedPaths={selectedFields}
                            onPreviewClick={onPreviewClick}
                        />
                    ))}
                </Box>

                {selectedFields.length > 0 && (
                    <Box
                        sx={{
                            mt: 2,
                            pt: 2,
                            borderTop: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Typography variant="subtitle2" gutterBottom>
                            Selected Paths ({selectedFields.length}):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedFields.map(field => (
                                <Chip
                                    key={field}
                                    label={field}
                                    size="small"
                                    onDelete={() => onFieldToggle(field)}
                                    color="primary"
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    )
}

export default JSONFieldSelector
